import { useState, type FormEvent } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';

type Status = 'idle' | 'submitting' | 'success' | 'error';

type LeadInquiryResponse = {
  ok?: boolean;
  lead_id?: string;
  error?: string;
  warning?: string;
  deduped?: boolean;
};

type SenderContext = {
  timezone: string | null;
  timezone_offset_minutes: number | null;
  timezone_source: 'browser_intl' | 'manual_override' | 'unknown';
  locale: string | null;
  submitted_at_local: string | null;
  submitted_at_utc: string;
  capture_version: 'tz-capture-v0.1';
};

export function ContactForm() {
  const [status, setStatus] = useState<Status>('idle');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [form, setForm] = useState({
    name: '',
    email: '',
    company: '',
    topic: '',
    message: '',
    sensitiveAck: false,
  });

  function update<K extends keyof typeof form>(key: K, value: (typeof form)[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setStatus('submitting');
    setErrorMsg(null);

    const submittedAt = new Date();
    const sourceUrl =
      typeof window !== 'undefined'
        ? `${window.location.origin}${window.location.pathname}#contact`
        : 'https://jlpatawaran.com/#contact';
    const topic = form.topic.trim() || 'General inquiry';
    const message = form.message.trim();

    try {
      const payload = {
        name: form.name.trim(),
        email: form.email.trim(),
        company: form.company.trim() || undefined,
        intent_shape: 'General inquiry',
        urgency: 'General inquiry',
        message,
        sensitive_data_ack: form.sensitiveAck,
        qualification_version: 'jlp-general-inquiry-v0.1',
        qualification_answers: {
          intent_shape: 'General inquiry',
          intent_detail: topic,
          selected_work_context: ['General inquiry'],
          work_context_detail: topic,
          desired_next_step: 'Manual reply',
          urgency: 'General inquiry',
          readiness_rating: 3,
          clarity_rating: 3,
          decision_role: 'Sender',
          optional_note_provided: Boolean(message),
          sender_context: captureSenderContext(submittedAt),
          sensitive_acknowledged: form.sensitiveAck,
          no_pricing_or_payment_commitment: true,
          auto_reply_status: 'future_capacity_gated',
        },
        qualification_score: 0,
        qualification_temperature: 'unscored',
        qualification_tags: [
          'source:jlpatawaran.com',
          'qualification:jlp-general-inquiry-v0.1',
          'route:general_inquiry',
          'general-inquiry',
        ],
        recommended_route: 'general_inquiry',
        recommended_next_action:
          'Manual JL review. Route to async prefit only if the note is actually a fit request.',
        missing_info: [],
        sensitive_risk_flag: detectSensitiveRisk(message),
        survey_completed_at: submittedAt.toISOString(),
        source_route: '/general-inquiry',
        source_url: sourceUrl,
      };
      const res = await fetch('/api/lead-inquiry', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = (await res.json().catch(() => ({}))) as LeadInquiryResponse;
      if (!res.ok) {
        throw new Error(data?.error || 'Could not send — try email directly');
      }
      setStatus('success');
      setForm({ name: '', email: '', company: '', topic: '', message: '', sensitiveAck: false });
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : 'Unknown error');
      setStatus('error');
    }
  }

  if (status === 'success') {
    return (
      <section id="contact" data-guide-label="General inquiry" className="border-b border-ink/5 bg-paper">
        <div className="mx-auto max-w-2xl px-6 py-24 text-center">
          <p className="font-display text-xs uppercase tracking-[0.25em] text-accent">
            Received
          </p>
          <h2 className="mt-3 font-display text-3xl tracking-tight md:text-4xl">
            Thanks — I’ll read it directly.
          </h2>
          <p className="mt-4 text-ink/70">
            This is in the same admin inbox as the async prefit, tagged as a
            general inquiry. If it needs the structured prefit path, I’ll route
            it there manually. You can also email{' '}
            <a href="mailto:jl@jlpatawaran.com" className="underline">
              jl@jlpatawaran.com
            </a>.
          </p>
        </div>
      </section>
    );
  }

  return (
    <section id="contact" data-guide-label="General inquiry" className="border-b border-ink/5 bg-paper">
      <div className="mx-auto max-w-2xl px-6 py-24">
        <p className="font-display text-xs uppercase tracking-[0.25em] text-accent">
          General inquiry
        </p>
        <h2 className="mt-3 font-display text-3xl tracking-tight md:text-4xl">
          Send a public-safe note.
        </h2>
        <p className="mt-4 text-ink/70">
          Use this for anything that is not an async prefit: introductions,
          writing, collaboration, questions, or context I should see. Keep it
          non-sensitive. Do not send files, screenshots, exports, credentials,
          private financial values, client names, or regulated data.
        </p>

        <form onSubmit={handleSubmit} className="mt-10 grid gap-6 sm:grid-cols-2">
          <div className="grid gap-2">
            <Label htmlFor="name">Name</Label>
            <Input
              id="name"
              required
              value={form.name}
              onChange={(e) => update('name', e.target.value)}
              disabled={status === 'submitting'}
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              required
              value={form.email}
              onChange={(e) => update('email', e.target.value)}
              disabled={status === 'submitting'}
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="company">Company (optional)</Label>
            <Input
              id="company"
              value={form.company}
              onChange={(e) => update('company', e.target.value)}
              disabled={status === 'submitting'}
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="topic">Topic (optional)</Label>
            <Input
              id="topic"
              value={form.topic}
              onChange={(e) => update('topic', e.target.value)}
              disabled={status === 'submitting'}
              placeholder="Intro, article, collaboration, question"
            />
          </div>
          <div className="grid gap-2 sm:col-span-2">
            <Label htmlFor="message">What should I know?</Label>
            <Textarea
              id="message"
              required
              minLength={10}
              value={form.message}
              onChange={(e) => update('message', e.target.value)}
              disabled={status === 'submitting'}
            />
          </div>
          <label className="flex gap-3 rounded-sm border border-ink/10 bg-white p-4 text-sm leading-relaxed text-ink/75 sm:col-span-2">
            <input
              type="checkbox"
              checked={form.sensitiveAck}
              onChange={(e) => update('sensitiveAck', e.target.checked)}
              className="mt-1 h-4 w-4 shrink-0 accent-ink"
              required
              disabled={status === 'submitting'}
            />
            <span>
              I confirm this general inquiry contains no confidential or regulated
              data, credentials, exports, screenshots, private records, client
              names, or private financial values.
            </span>
          </label>
          <div className="sm:col-span-2">
            <Button type="submit" size="lg" disabled={status === 'submitting'}>
              {status === 'submitting' ? 'Sending…' : 'Send general inquiry'}
            </Button>
            {status === 'error' && errorMsg && (
              <p className="mt-3 text-sm text-destructive">{errorMsg}</p>
            )}
          </div>
        </form>
      </div>
    </section>
  );
}

function detectSensitiveRisk(value: string): boolean {
  const normalized = value.toLowerCase();
  return [
    'api key',
    'bank statement',
    'client list',
    'confidential',
    'credential',
    'customer list',
    'password',
    'payroll',
    'private file',
    'screenshot',
    'tax return',
    'vendor list',
  ].some((term) => normalized.includes(term));
}

function captureSenderContext(now: Date = new Date()): SenderContext {
  try {
    const resolved = Intl.DateTimeFormat().resolvedOptions();
    const timezone = resolved.timeZone || null;
    const offsetMinutes = -now.getTimezoneOffset();
    return {
      timezone,
      timezone_offset_minutes: Number.isFinite(offsetMinutes) ? offsetMinutes : null,
      timezone_source: timezone ? 'browser_intl' : 'unknown',
      locale: typeof navigator !== 'undefined' ? navigator.language || null : null,
      submitted_at_local: formatLocalIsoWithOffset(now),
      submitted_at_utc: now.toISOString(),
      capture_version: 'tz-capture-v0.1',
    };
  } catch {
    return {
      timezone: null,
      timezone_offset_minutes: null,
      timezone_source: 'unknown',
      locale: null,
      submitted_at_local: null,
      submitted_at_utc: now.toISOString(),
      capture_version: 'tz-capture-v0.1',
    };
  }
}

function formatLocalIsoWithOffset(date: Date): string {
  const offsetMinutes = -date.getTimezoneOffset();
  const sign = offsetMinutes >= 0 ? '+' : '-';
  const absoluteOffset = Math.abs(offsetMinutes);
  const offsetHours = String(Math.floor(absoluteOffset / 60)).padStart(2, '0');
  const offsetRemainder = String(absoluteOffset % 60).padStart(2, '0');
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hour = String(date.getHours()).padStart(2, '0');
  const minute = String(date.getMinutes()).padStart(2, '0');
  const second = String(date.getSeconds()).padStart(2, '0');

  return `${year}-${month}-${day}T${hour}:${minute}:${second}${sign}${offsetHours}:${offsetRemainder}`;
}
